import type { JSONContent } from '@tiptap/react';
import { z } from 'zod';

import type { Column, TypedTable } from './model';
import { optionsFor, parseCell } from './table';

const key = z.string().min(1).max(500);

const cell = z.union([
  z.string().max(50000),
  z.number().finite(),
  z.boolean(),
  z.array(z.string().max(500)).max(1000),
  z.null(),
]);

const tableSchema = z
  .object({
    filters: z
      .array(
        z.object({
          column: key,
          operator: z.enum(['contains', 'equals', 'gt', 'lt', 'empty']),
          value: z.string().max(50000),
        }),
      )
      .max(100)
      .optional(),
    sorts: z
      .array(z.object({ column: key, direction: z.enum(['asc', 'desc']) }))
      .max(100)
      .optional(),
    columns: z
      .array(
        z
          .object({
            id: key,
            name: z.string().max(500),
            type: z.enum(['text', 'number', 'date', 'checkbox', 'select', 'multi', 'url']),
            options: z
              .array(z.union([key, z.object({ id: key, label: z.string().max(500) }).strict()]))
              .max(1000),
          })
          .strict(),
      )
      .min(1)
      .max(100),
    rows: z.array(z.object({ id: key, cells: z.record(key, cell) }).strict()).max(10000),
    sortColumn: z.string(),
    sortDirection: z.enum(['asc', 'desc']),
    filterColumn: z.string(),
    filterOperator: z.enum(['contains', 'equals', 'gt', 'lt', 'empty']),
    filterValue: z.string().max(50000),
  })
  .strict();

function unique(ids: string[], description: string) {
  if (new Set(ids).size !== ids.length) {
    throw new Error(`Duplicate ${description}`);
  }
}

export function validateTable(value: unknown): TypedTable {
  const table = tableSchema.parse(value);

  unique(
    table.columns.map(c => c.id),
    'column IDs',
  );

  unique(
    table.rows.map(r => r.id),
    'row IDs',
  );

  for (const col of table.columns) {
    const options = optionsFor(col);

    unique(
      options.map(o => o.id),
      'option IDs',
    );

    for (const row of table.rows) {
      const value = row.cells[col.id];

      if (value == null) {
        continue;
      }

      if (
        (col.type === 'number' && typeof value !== 'number') ||
        (col.type === 'checkbox' && typeof value !== 'boolean') ||
        (['text', 'url', 'date', 'select'].includes(col.type) && typeof value !== 'string') ||
        (col.type === 'multi' && !Array.isArray(value))
      ) {
        throw new Error(`Invalid typed value in ${col.name}`);
      }

      if (col.type === 'select' || col.type === 'multi') {
        for (const id of Array.isArray(value) ? value : [value]) {
          if (!options.some(o => o.id === id)) {
            throw new Error(`Unavailable option in ${col.name}`);
          }
        }
      } else {
        parseCell(col as Column, value as string | boolean);
      }
    }

    col.options = options;
  }

  for (const row of table.rows) {
    if (Object.keys(row.cells).some(id => !table.columns.some(c => c.id === id))) {
      throw new Error('A table cell has no column');
    }
  }

  for (const id of [
    table.sortColumn,
    table.filterColumn,
    ...(table.filters ?? []).map(f => f.column),
    ...(table.sorts ?? []).map(s => s.column),
  ]) {
    if (id && !table.columns.some(c => c.id === id)) {
      throw new Error('Unavailable view column');
    }
  }

  return table;
}

const attrs: Record<string, z.ZodType> = {
  heading: z.object({ level: z.number().int().min(1).max(6) }).strict(),
  codeBlock: z.object({ language: z.string().max(100).nullable().optional() }).strict(),
  orderedList: z
    .object({
      start: z.number().int().positive().optional(),
      type: z.string().nullable().optional(),
    })
    .strict(),
  taskItem: z.object({ checked: z.boolean() }).strict(),
  image: z
    .object({
      src: z
        .string()
        .max(7_000_000)
        .regex(
          /^(?:data:image\/(png|jpeg|webp|gif);base64,[A-Za-z0-9+/]+={0,2}|cairn-asset:[a-f0-9-]{36})$/,
        ),
      alt: z.string().max(2000).nullable().optional(),
      title: z.string().max(2000).nullable().optional(),
      width: z.number().positive().nullable().optional(),
      height: z.number().positive().nullable().optional(),
    })
    .strict(),
};

const block = [
  'paragraph',
  'heading',
  'blockquote',
  'bulletList',
  'orderedList',
  'taskList',
  'codeBlock',
  'image',
  'horizontalRule',
  'typedTable',
];

const children: Record<string, string[]> = {
  doc: block,
  paragraph: ['text', 'hardBreak'],
  heading: ['text', 'hardBreak'],
  codeBlock: ['text'],
  blockquote: block,
  bulletList: ['listItem'],
  orderedList: ['listItem'],
  listItem: block,
  taskList: ['taskItem'],
  taskItem: block,
};

const safeHref = (href: string) => /^(https?:|mailto:|tel:|#|\/(?!\/))/i.test(href);

export function validateDocument(value: unknown): JSONContent {
  let count = 0;

  function visit(value: unknown, depth: number): JSONContent {
    if (++count > 30000 || depth > 50) {
      throw new Error('This document exceeds the supported size or nesting limit');
    }

    const raw = z
      .object({
        type: z.string(),
        text: z.string().max(2_000_000).optional(),
        attrs: z.record(z.string(), z.unknown()).optional(),
        marks: z
          .array(
            z
              .object({ type: z.string(), attrs: z.record(z.string(), z.unknown()).optional() })
              .strict(),
          )
          .max(10)
          .optional(),
        content: z.array(z.unknown()).max(30000).optional(),
      })
      .strict()
      .parse(value);

    const type = raw.type;

    if (![...block, 'doc', 'text', 'hardBreak', 'listItem', 'taskItem'].includes(type)) {
      throw new Error(`Unsupported document node: ${type}`);
    }

    if (
      (type !== 'text' && raw.text !== undefined) ||
      (type === 'text' && (!raw.text || raw.content))
    ) {
      throw new Error('Invalid document text node');
    }

    const node: JSONContent = { type, ...(raw.text === undefined ? {} : { text: raw.text }) };

    if (type === 'typedTable') {
      node.attrs = { table: validateTable(raw.attrs?.table) };
    } else if (attrs[type]) {
      node.attrs = attrs[type].parse(raw.attrs ?? {}) as JSONContent['attrs'];
    } else if (raw.attrs && Object.keys(raw.attrs).length) {
      throw new Error(`Unsupported attributes on ${type}`);
    }

    if (raw.marks) {
      if (type !== 'text') {
        throw new Error('Only text can have formatting marks');
      }

      node.marks = raw.marks.map(mark => {
        if (['bold', 'italic', 'strike', 'underline', 'code'].includes(mark.type)) {
          if (mark.attrs && Object.keys(mark.attrs).length) {
            throw new Error('Unsupported formatting attributes');
          }

          return { type: mark.type };
        }

        if (mark.type !== 'link') {
          throw new Error('Unsupported formatting mark');
        }

        const link = z
          .object({
            href: z.string().max(10000).refine(safeHref, 'Unsafe link'),
            target: z.enum(['_blank', '_self']).nullable().optional(),
            rel: z.string().max(200).nullable().optional(),
            class: z.string().max(200).nullable().optional(),
            title: z.string().max(2000).nullable().optional(),
          })
          .strict()
          .parse(mark.attrs);

        return { type: 'link', attrs: { ...link, rel: 'noopener noreferrer nofollow' } };
      });
    }

    if (raw.content) {
      node.content = raw.content.map(child => visit(child, depth + 1));

      if (node.content.some(child => !children[type]?.includes(child.type!))) {
        throw new Error(`Invalid child inside ${type}`);
      }
    }

    return node;
  }

  const document = visit(value, 0);

  if (document.type !== 'doc') {
    throw new Error('A document needs a root');
  }

  return document;
}
